import _ from 'lodash';
import moment from 'moment';

export const MomentFormat12H = 'h:mm A';
export const MomentFormat24H = 'H:mm';
export const RegExp12HTimeFormat =
  /\b((1[0-2]|0?[1-9]):([0-5][0-9]) ([AaPp][Mm]))/;
export const RegExp24HTimeFormat = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

// CleanStr sets toLowerCase and strips away all white space.
export const CleanStr = (str) => {
  if (_.isString(str)) return str.toLowerCase().replace(/ /g, '');
  return '';
};

// DemangleUserInputtedTime transforms a time string entered by a user into
// something comprehensible by moment. It handles the following situations:
//   1. Where user has failed to enter a colon between numbers and moment's
//      parser fails. This is the case for [3-9]## as well as 1[3-9][0-9].
//   2. Where moment double-inputs the number of minutes when seeing
//      ##:#.
//
//  It also handles exceptions specific to unusual parsings in relation to
//  24 or 12 hour modes.
export const DemangleUserInputtedTime = (timeStr, is24h) => {
  const insert = (mainStr, insStr, pos) => {
    let posLocal = pos;
    let insStrLocal = insStr;
    if (_.isUndefined(posLocal)) posLocal = 0;
    if (_.isUndefined(insStrLocal)) insStrLocal = '';
    return mainStr.slice(0, pos) + insStrLocal + mainStr.slice(pos);
  };

  // Regexes.
  const missingColonRX = /\b([3-9]){1}([0-9]){1,2}\b$/;
  const oneWeirdInterpretationRX = /([1]){1}([0-5]){1}([0-9]){1}$/;
  const preventDoubleInputMomentRX = /([0-9]){1,2}:([0-9]){1}$/;
  const twelveHour2xRx = /([2]){1}([0-5]){1}/;
  const twelveHour2xxRx = /([2]){1}([0-5]){1}([0-9]){1}/;
  const twelveHour11xxRx = /([1]){1}([1]){1}([0-5]){1}([0-9]){1}/;
  const twelveHourxxxap = /([1-9]){1}([0-5]){1}([0-9]){1}([ap]){1}/;
  const twelveHourxxxxap = /([1]){1}([1-2]){1}([0-5]){1}([0-9]){1}([ap]){1}/;
  const twentyFourHour11xxRX = /([1]){1}([1]){1}([0-5]){1}([0-9]){1}/;
  const twentyFourHour2xxRX = /([2]){1}([0-5]){1}([0-9]){1}/;
  const twentyFourHour2xxxRX = /([2]){1}([0-3]){1}([0-5]){1}([0-9]){1}/;

  let timeStrDemangled = CleanStr(timeStr);

  // 12 hour time entry exceptions for 2x, 2xx, 11xx, and xxxa/p.
  if (!is24h && twelveHour2xRx.test(timeStrDemangled) &&
    timeStrDemangled.length === 2) {
    timeStrDemangled = insert(timeStrDemangled, ':', 1);
  }
  if (!is24h && twelveHour2xxRx.test(timeStrDemangled) &&
    timeStrDemangled.length === 3) {
    timeStrDemangled = insert(timeStrDemangled, ':', 1);
  }
  if (!is24h && twelveHour11xxRx.test(timeStrDemangled) &&
    timeStrDemangled.length === 4) {
    timeStrDemangled = insert(timeStrDemangled, ':', 2);
  }
  if (!is24h && twelveHourxxxxap.test(timeStrDemangled) &&
    timeStrDemangled.length >= 5) {
    timeStrDemangled = insert(timeStrDemangled, ':', 2);
  }
  if (!is24h && twelveHourxxxap.test(timeStrDemangled) &&
    timeStrDemangled.length >= 4) {
    timeStrDemangled = insert(timeStrDemangled, ':', 1);
  }

  // 24 hour time entry exceptions for 2xx, 11xx and 2xxx.
  if (is24h && twentyFourHour2xxRX.test(timeStrDemangled) &&
    timeStrDemangled.length === 3) {
    timeStrDemangled = insert(timeStrDemangled, ':', 1);
  }
  if (is24h &&
    (
      twentyFourHour2xxxRX.test(timeStrDemangled) ||
      twentyFourHour11xxRX.test(timeStrDemangled)
    ) &&
    timeStrDemangled.length === 4) {
    timeStrDemangled = insert(timeStrDemangled, ':', 2);
  }

  // Inject a colon if one appears to be missing.
  if ((missingColonRX.test(timeStrDemangled) ||
    oneWeirdInterpretationRX.test(timeStrDemangled)) &&
    !_.includes(timeStrDemangled, ':')) {
    timeStrDemangled = insert(timeStrDemangled, ':', 1);
  }

  // When the minutes indicated are unclear, attempt to fill in a reasonable
  // value for it.
  if (preventDoubleInputMomentRX.test(timeStrDemangled)) {
    const rightSide = Number(_.get(_.split(timeStrDemangled, ':'), '[1]', 0));
    if (_.isNaN(rightSide)) {
      timeStrDemangled = timeStrDemangled.slice(0, -1);
    }
    if (rightSide >= 0 && rightSide <= 5) {
      timeStrDemangled = `${timeStrDemangled}0`;
    }
  }

  // 24 hour time that is 3 characters long and missing a colon and the time
  // is above what could be valid for a 1:00-1:59 time.
  if (timeStrDemangled.length === 3 &&
    !_.includes(timeStrDemangled, ':') &&
    !_.includes(timeStrDemangled, 'a') &&
    !_.includes(timeStrDemangled, 'p')) {
    const timeStrAsNumber = Number(timeStrDemangled);
    if (!_.isNaN(timeStrAsNumber) && timeStrAsNumber > 159) {
      timeStrDemangled = insert(timeStrDemangled, ':', 2);
    }
  }

  // 24 hour time that is 4 characters long and missing a colon.
  if (timeStrDemangled.length === 4 &&
    !_.includes(timeStrDemangled, ':') &&
    !_.includes(timeStrDemangled, 'a') &&
    !_.includes(timeStrDemangled, 'p')) {
    const timeStrAsNumber = Number(timeStrDemangled);
    if (!_.isNaN(timeStrAsNumber) && timeStrAsNumber < 2400) {
      timeStrDemangled = insert(timeStrDemangled, ':', 2);
    }
  }

  return CleanStr(timeStrDemangled);
};

// hoursAsIntervals returns an array of strings for n minute intervals
// starting from some reference time and for a duration of hours.
const hoursAsIntervals = ({
  is24h = true,
  hours = 1,
  startHour = 0,
  startMinute = 0,
  intervalsMinute = 5,
}) => {
  const length = Math.floor(60 / intervalsMinute) * hours;
  return _.map(
    // [ 0, ... , n ] for 12 or 24 hours
    _.range(0, length),
    (num) =>
      moment(`${startHour}:${startMinute}`, 'HH:mm')
        .add(num * intervalsMinute, 'minutes')
        .format(is24h ? MomentFormat24H : MomentFormat12H),
  );
};

// HoursAs5MinuteIntervals returns an array of strings for five minute intervals
// starting from some reference time and for a duration of hours.
export const HoursAs5MinuteIntervals = (is24h, hours = 1, startHour = 0,
  startMinute = 0) =>
  hoursAsIntervals({
    is24h,
    hours,
    startHour,
    startMinute,
    intervalsMinute: 5,
  });

// HoursAs15MinuteIntervals returns an array of strings for fifthteen minute
// intervals starting from some reference time and for a duration of hours.
export const HoursAs15MinuteIntervals = (is24h, hours = 1, startHour = 0,
  startMinute = 0) =>
  hoursAsIntervals({
    is24h,
    hours,
    startHour,
    startMinute,
    intervalsMinute: 15,
  });

// HoursAs30MinuteIntervals returns an array of strings for thirty minute
// intervals starting from some reference time and for a duration of hours.
export const HoursAs30MinuteIntervals = (is24h, hours = 1, startHour = 0,
  startMinute = 0) =>
  hoursAsIntervals({
    is24h,
    hours,
    startHour,
    startMinute,
    intervalsMinute: 30,
  });

// HoursAs60MinuteIntervals returns an array of strings for 60  minute
// intervals starting from some reference time and for a duration of hours.
export const HoursAs60MinuteIntervals = (is24h, hours = 1, startHour = 0,
  startMinute = 0) =>
  hoursAsIntervals({
    is24h,
    hours,
    startHour,
    startMinute,
    intervalsMinute: 60,
  });

// TimeIntervalsGenerator generates a list of time intervals from a passed
// key entry string. If required, it will also generate a list of
// corresponding PM times for ambiguous strings (is12hPM).
// If a minute restriction is passed it will only generate valid times.
export const TimeIntervalsGenerator = (keyEntryOriginal, is24h, is12hPM,
  interval) => {
  const keyEntry = DemangleUserInputtedTime(keyEntryOriginal, is24h);
  const time = moment(keyEntry, is24h ? MomentFormat24H : MomentFormat12H);

  if (keyEntry === '') {
    switch (interval) {
      case 15:
        return HoursAs15MinuteIntervals(is24h, (is24h ? 24 : 12),
          (is12hPM ? 12 : 0), 0);

      case 60:
        return HoursAs60MinuteIntervals(is24h, (is24h ? 24 : 12),
          (is12hPM ? 12 : 0), 0);

      case 30:
      default:
        return HoursAs30MinuteIntervals(is24h, (is24h ? 24 : 12),
          (is12hPM ? 12 : 0), 0);
    }
  }

  const nextTimes = HoursAs15MinuteIntervals(is24h, (is24h ? 24 : 12),
    (is12hPM ? 12 : 0), 0);

  if (!time.isValid()) {
    return nextTimes;
  }

  // Match #:# and ##:#.
  let regExpMatch5MinShow =
    /^([0-9]{1}:[0-5]{1}){1}$|^([1-2]{1}[0-9]{1}:[0-5]{1}){1}$/;
  if (!is24h) {
    regExpMatch5MinShow =
      /^([1-9]{1}:[0-5]{1}){1}$|^([1]{1}[0-2]{1}:[0-5]{1}){1}$/;
  }

  // Match #:## and ##:##. These should be safe to just return.
  let regExpMatchExact =
    /^([0-9]{1}:[0-5]{1}[0-9]{1}){1}$|^([1-2]{1}[0-9]{1}:[0-5]{1}[0-9]{1}){1}$/;
  if (!is24h) {
    regExpMatchExact =
      /^([1-9]{1}:[0-5]{1}[0-9]{1}){1}$|^([1]{1}[0-2]{1}:[0-5]{1}[0-9]{1}){1}$/;
  }

  // Special exception: the time is in the list but has only one entry
  // e.g. 2:3 becomes 2:30 becomes ['2:30']. Add the next time plus 5
  // minutes.
  const inject5MinuteNextIfNeeded = (filtered) => {
    if (filtered && filtered.length === 1 &&
      regExpMatch5MinShow.test(keyEntryOriginal)) {
      const timeLocal = moment(filtered[0], is24h ? MomentFormat24H :
        MomentFormat12H);
      filtered.push(timeLocal.add(5, 'minute')
        .format(is24h ? MomentFormat24H : MomentFormat12H));
    }
  };

  // Handle short user input with a colon.
  if (_.includes(keyEntry, ':')) {
    const colonIdx = _.findIndex(keyEntry, (ch) => ch === ':');
    let filtered;

    // Colon at the end.
    if (colonIdx <= 2 && keyEntry.length <= 3 &&
      keyEntry.substring(keyEntry.length - 1, keyEntry.length) === ':') {
      filtered = _.filter(nextTimes, (t) =>
        _.includes(CleanStr(t).substring(0, colonIdx + 1), keyEntry));
    }

    // Long but matches the #:# and ##:# patterns?
    if (regExpMatch5MinShow.test(keyEntryOriginal)) {
      // Correct AM/PM swap with 12:x in 12h mode.
      // if ((time.hours() === 0 || time.hours() === 12) &&
      filtered = _.filter(nextTimes, (t) =>
        _.includes(
          CleanStr(t).substring(0, keyEntry.length),
          keyEntry));
    }
    inject5MinuteNextIfNeeded(filtered);

    if (filtered && filtered.length > 0) return filtered;
  }

  // Handle short user input without a colon by adding a colon to it.
  if (keyEntry.length <= 2 && !_.includes(keyEntry, ':') &&
    keyEntry !== '') {
    const colonated = `${keyEntry}:`;
    const colonIdx = _.findIndex(colonated, (ch) => ch === ':');
    const filtered = _.filter(nextTimes, (t) =>
      _.includes(CleanStr(t).substring(0, colonIdx + 1), colonated));
    inject5MinuteNextIfNeeded(filtered);

    if (filtered.length > 0) return filtered;
  }

  // We have a valid time, but it's not in the list of intervals
  // that was precomputed. Return an array containing that element
  // so that the user can select it.
  //
  // A second special exception is if it's 12 hour mode and we need
  // to give both AM/PM versions of the time.
  let timeFormatted = time.format(is24h ?
    MomentFormat24H : MomentFormat12H);

  const hasAmPm = !is24h &&
    (_.includes(keyEntry, 'a') || _.includes(keyEntry, 'p'));
  if (!is24h && !is12hPM && time.hours() === 12 && !hasAmPm) {
    timeFormatted = time.subtract(12, 'hour').format(MomentFormat12H);
  }
  if (!is24h && is12hPM && time.hours() !== 12 && !hasAmPm) {
    timeFormatted = time.add(12, 'hour').format(MomentFormat12H);
  }
  if (!_.find(nextTimes, (t) => CleanStr(t) === CleanStr(timeFormatted)) ||
    regExpMatchExact.test(keyEntry) ||
    regExpMatchExact.test(keyEntryOriginal)) {
    const filtered = [timeFormatted];
    inject5MinuteNextIfNeeded(filtered);

    return filtered;
  }

  if (hasAmPm &&
    _.find(nextTimes, (t) => CleanStr(t) === CleanStr(timeFormatted))) {
    return [_.find(nextTimes, (t) => CleanStr(t) === CleanStr(timeFormatted))];
  }

  return nextTimes;
};

// TimeStrIs30MinInterval bisects a string time, extracts the number of
// minutes, and determines whether or not they are 0 or 30 minute time
// intervals.
export const TimeStrIs30MinInterval = (timeStr) => {
  const rightSide = _.get(_.split(timeStr, ':'), '[1]');
  if (rightSide && _.isString(rightSide)) {
    const minutesStr = rightSide.substring(0, 2);
    const minutes = Number(minutesStr);
    if (minutes % 30 === 0) {
      return true;
    }
  }
  return false;
};

export const TwelveHourTimeLocales = [
  'en', 'en-au', 'en-ca', 'en-ie', 'en-nz', 'en-za', 'en-gb', 'en-us',
  'en-ph', 'es-co', 'ar-eg', 'ar-sa', 'pa-pk', 'ms',
];
